import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UpdateUserRoleInput {
  profileId: string;
  roleId: string;
  roleName: string; // For backwards compatibility with profiles.role
}

interface UpdateUserRoleResult {
  success: boolean;
  profile_id: string;
  new_role_id: string;
  new_role_name: string;
}

/**
 * Hook to update a user's role assignment.
 * Uses SECURITY DEFINER RPC function that:
 * - Checks manage_employee_roles permission
 * - Prevents self-role changes
 * - Updates both role_id and role atomically
 * - Creates audit log entry
 */
export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ profileId, roleId, roleName }: UpdateUserRoleInput) => {
      const { data, error } = await supabase.rpc('update_user_role', {
        p_profile_id: profileId,
        p_role_id: roleId,
        p_role_name: roleName,
      });

      if (error) {
        console.error('[useUpdateUserRole] Failed to update role:', error);
        throw new Error(error.message || 'Failed to update role');
      }

      return data as unknown as UpdateUserRoleResult;
    },
    onSuccess: (result) => {
      toast.success('Role updated successfully');

      // Invalidate relevant caches
      queryClient.invalidateQueries({ queryKey: ['team', 'members'] });
      queryClient.invalidateQueries({ queryKey: ['team', 'member-profile', result.profile_id] });
      queryClient.invalidateQueries({ queryKey: ['role-user-count'] });
      queryClient.invalidateQueries({ queryKey: ['user-permissions'] });
    },
    onError: (error: Error) => {
      console.error('[useUpdateUserRole] Error:', error);
      
      // Provide user-friendly error messages
      let message = error.message;
      if (message.includes('manage_employee_roles')) {
        message = 'You do not have permission to assign roles';
      } else if (message.includes('Cannot change your own role')) {
        message = 'You cannot change your own role';
      } else if (message.includes('not in same company')) {
        message = 'User not found';
      }
      
      toast.error(message);
    },
  });
};
